import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams, useParams } from 'react-router-dom';
import SearchField from 'components/Common/SearchField';
import { InfoIcon, FilterIcon } from 'components/CommonIcons/Icons';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Grid,
  GlobalStyles,
  CircularProgress,
  Button,
  Select,
  MenuItem,
  FormControl,
  TextField,
  Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { getDataSource } from './services/dataSource';
import { DataBundle } from './types';
import CommonDrawer from 'components/Common/CommonDrawer';
import DrawerHeader from 'components/Common/DrawerHeader';
import DrawerFooter from 'components/Common/DrawerFooter';
import FilterMultiSelectStyled from 'components/FilterDrawer/FilterMultiSelectStyled';
import Legend from './crossflow/Legend';
import GraphView from './crossflow/GraphView';
import ReportsTab from './crossflow/ReportsTab';
import Inspector from './crossflow/Inspector';
import { useDashboardFilterPersistence } from 'hooks/useDashboardFilterPersistence';
import { deleteDashboardFilters } from 'service/service';

const DASHBOARD_CONFIG = {
  dashboardId: 'crossflow-navigator',
  dashboardName: 'CrossFlow Navigator',
  pageName: 'CrossFlow Navigator Page'
};


const customFilterFields = [
  { label: 'Subtype', value: 'Subtype' },
  { label: 'Stage', value: 'Stage' },
  { label: 'Qualified Name', value: 'QualifiedName' },
  { label: 'Environment', value: 'Env' },
  { label: 'Owner', value: 'Owner' },
  { label: 'Application', value: 'Application' },
];

const getInitialFilters = () => {
  return {
    availableFlows: null,
    domains: null,
    sectors: null,
    programs: null,
    edgeLabels: true,
    nodeTypes: {},
    additionalFilters: [],
  };
};

const PlaceholderView: React.FC = () => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        width: '100%',
        px: 3,
        textAlign: 'center',
      }}
    >
      <Typography
        variant="body1"
        sx={{
          color: '#5e6b79',
          fontSize: '16px',
          fontWeight: 300,
        }}
      >
        Select filters to view lineage
      </Typography>
    </Box>
  );
};

export default function CrossFlowNavigatorPage() {
  const { tab: paramTab } = useParams();
  const [searchParams] = useSearchParams();
  let tab = paramTab || searchParams.get('tab') || 'lineageMap';
  if (tab === 'lineage') {
    tab = 'lineageMap';
  }

  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [isInfoDrawerOpen, setIsInfoDrawerOpen] = useState(false);
  const defaultOptions = [{ label: 'All', value: 'all' }];
  const [appliedFilters, setAppliedFilters] = useState<any>(getInitialFilters());
  const [activeFilters, setActiveFilters] = useState<any>(getInitialFilters());

  const [isFiltersLoaded, setIsFiltersLoaded] = useState(false);

  const handleSavedFiltersLoaded = useCallback((savedFilters: any) => {
    console.log('Loading saved filters for CrossFlow:', savedFilters);
    if (savedFilters && Object.keys(savedFilters).length > 0) {
      setAppliedFilters(savedFilters);
      setActiveFilters(savedFilters);

      setIsLoading(true);
      getDataSource()
        .load(savedFilters)
        .then((newBundle) => {
          setFilteredBundle(newBundle);
          setHasAppliedFilters(true);
          setIsLoading(false);
        })
        .catch((err) => {
          console.error('Failed to load default crossflow data on init', err);
          setIsLoading(false);
        });
    }
    setIsFiltersLoaded(true);
  }, []);

  const {
    isLoading: isFiltersLoading,
    isSaving: isSavingFilters,
    hasSavedFilters,
    savedFilters: savedDefaultFilters,
    manualSave: manualSaveFilters,
    resetToSaved: resetToSavedFilters,
  } = useDashboardFilterPersistence({
    config: DASHBOARD_CONFIG,
    currentFilters: appliedFilters,
    onFiltersLoaded: handleSavedFiltersLoaded,
    debounceDelay: 1000,
    enabled: true
  });

  const [isLoading, setIsLoading] = useState(false);
  const [tooManyNodes, setTooManyNodes] = useState(false);
  const [graphElements, setGraphElements] = useState<any[]>([]);
  const [viewportNonce, setViewportNonce] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isClearingIcon, setIsClearingIcon] = useState(false);
  const [portalTarget, setPortalTarget] = useState<Element | null>(null);

  useEffect(() => {
    let attempt = 0;
    const findPortal = () => {
      const target = document.getElementById('crossflow-portal');
      if (target) {
        setPortalTarget(target);
      } else if (attempt < 20) {
        attempt++;
        setTimeout(findPortal, 50);
      }
    };
    findPortal();
  }, []);

 const [filterOptions, setFilterOptions] = useState<{ domains: string[]; sectors: string[]; programs: string[]; nodeTypes: string[]; availableFlows: string[] } | null>(null);
  const [filteredBundle, setFilteredBundle] = useState<DataBundle | null>(null);
  const [hasAppliedFilters, setHasAppliedFilters] = useState(false);
  const [selectedNode, setSelectedNode] = useState<any | null>(null);

  useEffect(() => {
    getDataSource()
      .loadFilters()
      .then((data) => setFilterOptions({ ...data, availableFlows: data.availableFlows || [] }))
      .catch((error) => console.error('Failed to load crossflow filters', error));
  }, []);

  const workerRef = useRef<Worker | null>(null);

  useEffect(() => {
    workerRef.current = new Worker(new URL('./graph.worker.ts', import.meta.url));
    workerRef.current.onmessage = (event) => {
      const { type, payload, error } = event.data;
      if (type === 'SUCCESS') {
        setGraphElements(payload.elements);
        setTooManyNodes(false);
        setIsLoading(false);
        setViewportNonce((prev) => prev + 1);
      } else if (type === 'TOO_MANY_NODES') {
        setTooManyNodes(true);
        setGraphElements([]);
        setIsLoading(false);
      } else if (type === 'ERROR') {
        console.error('Worker error:', error);
        setIsLoading(false);
      }
    };

    return () => {
      workerRef.current?.terminate();
    };
  }, []);

  useEffect(() => {
    if (!filteredBundle || !isFiltersLoaded) return;
    setIsLoading(true);
    setTooManyNodes(false);

    workerRef.current?.postMessage({
      bundle: filteredBundle,
      activeFilters,
    });
  }, [filteredBundle, activeFilters, isFiltersLoaded]);

  const availableFlowOptions = useMemo(() => {
    const defaultOpt = [{ label: 'All', value: 'all' }];
    if (!filterOptions || !filterOptions.availableFlows) return defaultOpt;
    return [...defaultOpt, ...filterOptions.availableFlows.map((u) => ({ label: u, value: u }))];
  }, [filterOptions]);

  const domainOptions = useMemo(() => {
    const defaultOpt = [{ label: 'All', value: 'all' }];
    if (!filterOptions) return defaultOpt;
    return [...defaultOpt, ...filterOptions.domains.map((u) => ({ label: u, value: u }))];
  }, [filterOptions]);

  const sectorOptions = useMemo(() => {
    const defaultOpt = [{ label: 'All', value: 'all' }];
    if (!filterOptions) return defaultOpt;
    return [...defaultOpt, ...filterOptions.sectors.map((u) => ({ label: u, value: u }))];
  }, [filterOptions]);

  const programOptions = useMemo(() => {
    const defaultOpt = [{ label: 'All', value: 'all' }];
    if (!filterOptions) return defaultOpt;
    return [...defaultOpt, ...filterOptions.programs.map((u) => ({ label: u, value: u }))];
  }, [filterOptions]);

  const nodeTypeOptions = useMemo(() => {
    if (!filterOptions) return [];
    return filterOptions.nodeTypes.map((u) => {
      // Capitalize first letter and handle special cases
      let label = u.charAt(0).toUpperCase() + u.slice(1);
      if (u === 'dq') label = 'DQ Suites';
      if (u === 'recon') label = 'Recon Suites';
      return { label, key: u };
    });
  }, [filterOptions]);

  const displayedGraphElements = useMemo(() => {
    const query = searchText.trim().toLowerCase();

    if (!query) return graphElements;

    const matchedNodeIds = new Set<string>();

    graphElements.forEach((element: any) => {
      const data = element?.data;

      if (!data?.id || data?.source || data?.target) return;

      const searchableText = Object.values(data)
        .filter((value) => value !== null && value !== undefined)
        .map((value) => {
          if (typeof value === 'string') return value;
          if (typeof value === 'number' || typeof value === 'boolean') return String(value);
          return JSON.stringify(value);
        })
        .join(' ')
        .toLowerCase();

      if (searchableText.includes(query)) {
        matchedNodeIds.add(data.id);
      }
    });

    return graphElements.filter((element: any) => {
      const data = element?.data;

      if (!data) return false;

      if (data.source && data.target) {
        return matchedNodeIds.has(data.source) && matchedNodeIds.has(data.target);
      }

      return matchedNodeIds.has(data.id);
    });
  }, [graphElements, searchText]);


  useEffect(() => {
    if (isFilterDrawerOpen) {
      setAppliedFilters(activeFilters);
    }
  }, [isFilterDrawerOpen, activeFilters]);

  const isFiltersCleared = useMemo(() => {
    const base = getInitialFilters();
    return JSON.stringify(appliedFilters) === JSON.stringify(base);
  }, [appliedFilters]);

  const hasFiltersChanged = useMemo(() => {
    return !isFiltersCleared;
  }, [isFiltersCleared]);


  const filtersMatchSavedDefault = useMemo(() => {
    if (!savedDefaultFilters) return false;
    return JSON.stringify(appliedFilters) === JSON.stringify(savedDefaultFilters);
  }, [appliedFilters, savedDefaultFilters]);

  const showSaveAsDefault = useMemo(() => {
    if (filtersMatchSavedDefault) return false;
    if (hasSavedFilters) return !filtersMatchSavedDefault;
    return hasFiltersChanged;
  }, [hasFiltersChanged, filtersMatchSavedDefault, hasSavedFilters]);

  const showResetToDefault = useMemo(() => {
    if (hasSavedFilters && savedDefaultFilters) {
      return !filtersMatchSavedDefault;
    }
    return hasFiltersChanged;
  }, [hasSavedFilters, savedDefaultFilters, filtersMatchSavedDefault, hasFiltersChanged]);

  const saveButtonText = hasSavedFilters ? 'SAVE AS NEW DEFAULT' : 'SAVE AS DEFAULT';

  const handleSaveAsDefault = async () => {
    if (isFiltersCleared) {
      // When filters are in the initial/cleared state, delete from backend directly
      try {
        await deleteDashboardFilters(DASHBOARD_CONFIG.dashboardId);
        console.log('Default filters cleared from backend successfully');
      } catch (err) {
        console.error('Failed to clear default filters from backend', err);
      }
    } else {
      const result = await manualSaveFilters(appliedFilters);
      if (result?.success) {
        console.log('Filters saved as default successfully');
      }
    }
  };

  const handleResetToDefault = async () => {
    if (hasSavedFilters && savedDefaultFilters) {
      const result = await resetToSavedFilters();
      if (result?.success) {
        console.log('Reset to default filters');
        setIsFilterDrawerOpen(false);
        const defaultFilters = savedDefaultFilters;
        setAppliedFilters(defaultFilters);
        setActiveFilters(defaultFilters);
        setIsLoading(true);
        getDataSource()
          .load(defaultFilters)
          .then((newBundle) => {
            setFilteredBundle(newBundle);
            setHasAppliedFilters(true);
            setIsLoading(false);
          })
          .catch((err) => {
            console.error('Failed to load default crossflow data', err);
            setIsLoading(false);
          });
      }
    } else {
      const defaultState = {
        availableFlows: [defaultOptions[0]],
        domains: [defaultOptions[0]],
        sectors: [defaultOptions[0]],
        programs: [defaultOptions[0]],
        edgeLabels: true,
        nodeTypes: Object.fromEntries(nodeTypeOptions.map((opt) => [opt.key, true])),
        additionalFilters: [],
      };
      setAppliedFilters(defaultState);
      setActiveFilters(defaultState);
      setIsFilterDrawerOpen(false);
      setIsLoading(true);
      getDataSource()
        .load(defaultState)
        .then((newBundle) => {
          setFilteredBundle(newBundle);
          setHasAppliedFilters(true);
          setIsLoading(false);
        })
        .catch((err) => {
          console.error('Failed to load base default crossflow data', err);
          setIsLoading(false);
        });
    }
  };

  const handleAddCustomFilter = () => {
    const id = crypto.randomUUID();
    setAppliedFilters((prev: any) => ({
      ...prev,
      additionalFilters: [...(prev.additionalFilters || []), { id, field: '', value: '' }],
    }));
  };

  const handleUpdateCustomFilter = (id: string, changes: Partial<{ field: string; value: string }>) => {
    setAppliedFilters((prev: any) => ({
      ...prev,
      additionalFilters: (prev.additionalFilters || []).map((f: any) =>
        f.id === id ? { ...f, ...changes } : f
      ),
    }));
  };

  const handleRemoveCustomFilter = (id: string) => {
    setAppliedFilters((prev: any) => ({
      ...prev,
      additionalFilters: (prev.additionalFilters || []).filter((f: any) => f.id !== id),
    }));
  };

  const handleApplyFilters = () => {
    // If filters are in the initial/cleared state, show placeholder instead of loading data
    const base = getInitialFilters();
    const isClearedState = JSON.stringify(appliedFilters) === JSON.stringify(base);

    if (isClearedState) {
      setActiveFilters(appliedFilters);
      setIsFilterDrawerOpen(false);
      setFilteredBundle(null);
      setGraphElements([]);
      setHasAppliedFilters(false);
      setSelectedNode(null);
      setSearchText('');
      setTooManyNodes(false);
      return;
    }

    setActiveFilters(appliedFilters);
    setIsFilterDrawerOpen(false);
    setIsLoading(true);
    setTooManyNodes(false);
    getDataSource()
      .load(appliedFilters)
      .then((newBundle) => {
        setFilteredBundle(newBundle);
        setHasAppliedFilters(true);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error('Failed to load filtered crossflow data', err);
        setIsLoading(false);
      });
  };

  const handleReset = () => {
    const defaultState = getInitialFilters();
    setAppliedFilters(defaultState);
    setActiveFilters(defaultState);
    setFilteredBundle(null);
    setGraphElements([]);
    setHasAppliedFilters(false);
    setSelectedNode(null);
    setSearchText('');
    setTooManyNodes(false);
    setIsFilterDrawerOpen(false);
  };

  const handleSelectChange = (field: string, newValue: any[]) => {
    setAppliedFilters((prev: any) => {
      const oldValue = prev[field] || [];
      const hasAllOld = oldValue.some((v: any) => v.value === 'all');
      const hasAllNew = newValue.some((v: any) => v.value === 'all');

      let finalValue = newValue;

      if (hasAllOld && hasAllNew) {
        if (newValue.length < oldValue.length) {
          const clickedOption = oldValue.find((o: any) => !newValue.some((n: any) => n.value === o.value));
          if (clickedOption && clickedOption.value !== 'all') {
            finalValue = [clickedOption];
          }
        } else if (newValue.length > oldValue.length) {
          const clickedOption = newValue.find((n: any) => !oldValue.some((o: any) => o.value === n.value));
          if (clickedOption && clickedOption.value !== 'all') {
            finalValue = [clickedOption];
          }
        }
      }

      return { ...prev, [field]: finalValue };
    });
  };

  const handleCheckboxChange = (field: string, checked: boolean) => {
    setAppliedFilters((prev: any) => ({ ...prev, [field]: checked }));
  };

  const handleNodeTypeChange = (type: string, checked: boolean) => {
    setAppliedFilters((prev: any) => ({
      ...prev,
      nodeTypes: {
        ...prev.nodeTypes,
        [type]: checked,
      },
    }));
  };

  useEffect(() => {
    const handleToggle = () => setIsFilterDrawerOpen((prev) => !prev);
    globalThis.addEventListener('toggleCrossFlowFilterDrawer', handleToggle);
    return () => globalThis.removeEventListener('toggleCrossFlowFilterDrawer', handleToggle);
  }, []);

  let lineageMapContent: React.ReactNode;

  if (isLoading || isFiltersLoading) {
    lineageMapContent = (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          gap: 2,
        }}
      >
        <CircularProgress sx={{ color: '#003771' }} />
        <Typography sx={{ color: '#003771', fontWeight: 600 }}>
          Loading lineage graph...
        </Typography>
      </Box>
    );
  } else if (hasAppliedFilters) {
    lineageMapContent = (
    <GraphView
          elements={displayedGraphElements}
          edgeLabels={activeFilters.edgeLabels}
          onSelectNode={(node) => {
            const nodeData = node?.data || node;

            setSelectedNode({
              ...node,
              data: nodeData,
              group: node?.group || nodeData?.group || nodeData?.AssetType || 'nodes',
              label: node?.label || nodeData?.label || nodeData?.Name || nodeData?.id,
            });
          }}
          onBackgroundTap={() => console.log('Background tapped')}
          viewportNonce={viewportNonce}
        />
    );
  }

  return (
    <div
      style={{
        height: 'calc(100vh - 10px)',
        display: 'flex',
        flexDirection: 'column',
        background: '#f9fafb',
        position: 'relative',
      }}
    >
      <GlobalStyles
        styles={{
          '.filter-drawer-wrapper .MuiMenu-paper': {
            backgroundColor: '#FFFFFF !important',
          },
          '.filter-drawer-wrapper .MuiPopover-root .MuiPaper-root': {
            backgroundColor: '#FFFFFF !important',
          },
        }}
      />
      <CommonDrawer
        className="filter-drawer-wrapper"
        open={isFilterDrawerOpen}
        onClose={() => setIsFilterDrawerOpen(false)}
        backgroundColor="#FFFFFF"
        sx={{
          '& .MuiDrawer-paper': {
            width: '450px !important',
            maxWidth: '450px !important',
            height: 'calc(100vh - 40px) !important',
            top: '40px !important',
            backgroundColor: '#FFFFFF !important',
            overflow: 'hidden !important',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            backgroundColor: '#FFFFFF',
          }}
        >
          <Box
            sx={{
              '& .MuiTypography-root': { color: '#003771 !important' },
              '& .MuiSvgIcon-root': { color: '#003771 !important' },
              '& .MuiButton-root': { color: '#003771 !important' },
              '& > div > div:first-of-type': {
                borderBottom: 'none',
                pb: 0,
              },
              borderBottom: '1px solid #003771',
            }}
          >
            <DrawerHeader
              title="Filters"
              onClose={() => setIsFilterDrawerOpen(false)}
              onResetDefault={handleResetToDefault}
              onSaveAsDefault={handleSaveAsDefault}
              resetDefaultText="RESET TO DEFAULT"
              saveText={saveButtonText}
              showSaveAsDefault={showSaveAsDefault}
              showResetToDefault={showResetToDefault}
              isSaving={isSavingFilters}
            />
          </Box>

          <Box sx={{ flex: 1, overflowY: 'auto', px: 3, py: 2 }}>
            <Box
              sx={{
                '& .MuiTypography-root': { color: '#333 !important' },
                '& .MuiSelect-select': { backgroundColor: '#EFF6FF' },
              }}
            >
              <FilterMultiSelectStyled
                label="Domains"
                options={domainOptions}
                value={appliedFilters.domains || []}
                onChange={(val) => handleSelectChange('domains', val)}
                width={"100%" as any}
              />

              <FilterMultiSelectStyled
                label="Sectors"
                options={sectorOptions}
                value={appliedFilters.sectors || []}
                onChange={(val) => handleSelectChange('sectors', val)}
                width={"100%" as any}
              />

              <FilterMultiSelectStyled
                label="Programs"
                options={programOptions}
                value={appliedFilters.programs || []}
                onChange={(val) => handleSelectChange('programs', val)}
                width={"100%" as any}
              />

              <FilterMultiSelectStyled
                label="Available Flows"
                options={availableFlowOptions}
                value={appliedFilters.availableFlows || []}
                onChange={(val) => handleSelectChange('availableFlows', val)}
                width={"100%" as any}
              />
            </Box>

            <Box sx={{ mt: 2, mb: 3 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={appliedFilters.edgeLabels || false}
                    onChange={(e) => handleCheckboxChange('edgeLabels', e.target.checked)}
                    sx={{
                      color: '#39ACE7',
                      '&.Mui-checked': { color: '#39ACE7' },
                      '& .MuiSvgIcon-root': { fontSize: 24 },
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: '14px', color: '#333' }}>Edge Labels</Typography>
                }
              />
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography
                sx={{ color: '#003771 !important', fontSize: '14px', fontWeight: 600, mb: 1 }}
              >
                Node Types
              </Typography>
              <Grid container spacing={1}>
                {nodeTypeOptions.map((item) => (
                  <Grid item xs={4} key={item.key}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={appliedFilters.nodeTypes?.[item.key] !== false} // Default to true if undefined
                          onChange={(e) => handleNodeTypeChange(item.key, e.target.checked)}
                          sx={{
                            color: '#39ACE7',
                            '&.Mui-checked': { color: '#39ACE7' },
                            '& .MuiSvgIcon-root': { fontSize: 24 },
                          }}
                        />
                      }
                      label={
                        <Typography
                          sx={{
                            fontSize: '12px',
                            color: '#333 !important',
                            textTransform: 'capitalize',
                          }}
                        >
                          {item.label}
                        </Typography>
                      }
                      sx={{ m: 0 }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Box sx={{ mb: 2, mt: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography sx={{ color: '#003771 !important', fontSize: '14px', fontWeight: 600 }}>
                  Additional Filters
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleAddCustomFilter}
                  sx={{
                    textTransform: 'uppercase',
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    color: '#003771',
                    borderColor: '#a3c2e6',
                    borderRadius: '20px',
                    padding: '3px 14px',
                    '&:hover': {
                      borderColor: '#003771',
                      backgroundColor: 'rgba(0, 55, 113, 0.04)',
                    }
                  }}
                >
                  ADD FILTER
                </Button>
              </Box>

              {(appliedFilters.additionalFilters || []).map((filter: any) => {
                const shouldShowTooltip = (filter.value?.length ?? 0) > 15;
                return (
                  <Box
                    key={filter.id}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr auto',
                      gap: 2,
                      alignItems: 'center',
                      mb: 1.5
                    }}
                  >
                    <FormControl fullWidth size="small" variant="outlined">
                      <Select
                        displayEmpty
                        value={filter.field}
                        onChange={(e) =>
                          handleUpdateCustomFilter(filter.id, { field: e.target.value })
                        }
                        IconComponent={ExpandMoreIcon}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              mt: 0.5,
                              backgroundColor: '#FFFFFF !important',
                              color: '#003771 !important',
                              borderRadius: '4px',
                              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.16)',
                              '& .MuiMenuItem-root': {
                                color: '#003771 !important',
                                backgroundColor: '#FFFFFF !important',
                                fontSize: '13px',
                                minHeight: '32px',
                                '&:hover': {
                                  backgroundColor: '#EFF6FF !important',
                                },
                                '&.Mui-selected': {
                                  backgroundColor: '#E1F5FE !important',
                                },
                                '&.Mui-selected:hover': {
                                  backgroundColor: '#E1F5FE !important',
                                },
                              },
                            },
                          },
                          MenuListProps: {
                            sx: {
                              py: 0,
                              backgroundColor: '#FFFFFF !important',
                            },
                          },
                        }}
                        renderValue={(selected) => {
                          if (!selected) {
                            return (
                              <span style={{ color: '#003771', opacity: 0.8, fontSize: '13px' }}>
                                Field
                              </span>
                            );
                          }
                          return (
                            customFilterFields.find((o) => o.value === selected)?.label || selected
                          );
                        }}
                        sx={{
                          height: '38px',
                          backgroundColor: '#EFF6FF',
                          borderRadius: '8px',
                          fontSize: '13px',
                          '& .MuiSelect-select': {
                            color: '#003771',
                            paddingTop: '8px',
                            paddingBottom: '8px',
                            fontWeight: 500,
                          },
                          '& .MuiOutlinedInput-notchedOutline': {
                            border: 'none',
                          },
                          '& .MuiSelect-icon': {
                            color: '#39ACE7',
                          }
                        }}
                      >
                        {customFilterFields.map((option) => (
                          <MenuItem
                            key={option.value}
                            value={option.value}
                            sx={{
                              color: '#003771',
                              fontSize: '13px',
                              '&.Mui-selected': {
                                backgroundColor: '#E1F5FE !important',
                              }
                            }}
                          >
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <Tooltip
                      title={shouldShowTooltip ? filter.value : ''}
                      placement="bottom"
                      enterDelay={500}
                      leaveDelay={200}
                      arrow
                    >
                      <div style={{ width: '100%' }}>
                        <TextField
                          placeholder="Value"
                          value={filter.value}
                          onChange={(e) => handleUpdateCustomFilter(filter.id, { value: e.target.value })}
                          fullWidth
                          size="small"
                          sx={{
                            '& .MuiInputBase-root': { height: '38px', borderRadius: '8px', backgroundColor: 'transparent' },
                            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                            '& .MuiInputBase-input::placeholder': {
                              color: '#003771',
                              opacity: 0.8,
                              fontWeight: 500,
                            },
                          }}
                          inputProps={{
                            style: {
                              color: '#003771',
                              fontSize: '13px',
                              fontWeight: 500,
                            }
                          }}
                        />
                      </div>
                    </Tooltip>

                    <Button
                      variant="text"
                      onClick={() => handleRemoveCustomFilter(filter.id)}
                      sx={{
                        textTransform: 'uppercase',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        color: '#00a8e8',
                        padding: '4px 8px',
                        minWidth: 'auto',
                        '&:hover': {
                          backgroundColor: 'transparent',
                          color: '#007cc0'
                        }
                      }}
                    >
                      REMOVE
                    </Button>
                  </Box>
                );
              })}
            </Box>
          </Box>

          <Box
            sx={{
              borderTop: '1px solid #E0E0E0',
            }}
          >
            <DrawerFooter
              onReset={handleReset}
              onApply={handleApplyFilters}
              applyText="APPLY"
              resetText="CLEAR FILTERS"
            />
          </Box>
        </Box>
      </CommonDrawer>

      {/* Content area that responds to the global TabPageHeader */}
      <div style={{ flex: 1, position: 'relative', overflow: 'hidden', paddingTop: '44px' }}>
        {tab === 'lineageMap' && (
          <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
            {lineageMapContent}
          </Box>
        )}

        {tab !== 'lineageMap' && (
          <Box sx={{ width: '100%', height: '100%', overflow: 'auto', backgroundColor: '#f9fafb' }}>
            {tab === 'reports' &&
              (hasAppliedFilters ? <ReportsTab bundle={filteredBundle} /> : <PlaceholderView />)}
            {tab === 'clusterPanel' && <div style={{ padding: '20px' }} />}
            {tab === 'admin' && <div style={{ padding: '20px' }} />}
          </Box>
        )}
      </div>

      {filteredBundle && (
        <CommonDrawer
          className="filter-drawer-wrapper inspector-drawer-wrapper"
          open={!!selectedNode || isInfoDrawerOpen}
          onClose={() => {
            setSelectedNode(null);
            setIsInfoDrawerOpen(false);
          }}
          anchor="right"
          backgroundColor="#FFFFFF"
          sx={{
            '& .MuiDrawer-paper': {
              width: '450px !important',
              maxWidth: '450px !important',
              height: 'calc(100vh - 40px) !important',
              top: '40px !important',
              backgroundColor: '#FFFFFF !important',
              overflow: 'hidden !important',
            },
          }}
        >
          <Inspector
            open={!!selectedNode || isInfoDrawerOpen}
            onClose={() => {
              setSelectedNode(null);
              setIsInfoDrawerOpen(false);
            }}
            node={selectedNode}
            businessImpact={null}
            dependents={null}
            bundle={filteredBundle}
            onSelectAssetId={(id) => {
              const targetNode = graphElements.find(
                (el) => el?.data?.id === id || el?.data?.AssetId === id,
              );

              if (targetNode) {
                setSelectedNode({
                  data: targetNode.data,
                  group: targetNode.data.group || targetNode.data.AssetType || 'nodes',
                  label: targetNode.data.label || targetNode.data.Name || targetNode.data.id,
                });
              }
            }}
          />
        </CommonDrawer>
      )}

      {tab === 'lineageMap' && <Legend />}

      {portalTarget && createPortal(
        <>
          <SearchField
            value={searchText}
            type="crossflow"
            onChange={setSearchText}
            onSearch={() => {
              setIsSearching(true);
              setTimeout(() => setIsSearching(false), 500);
            }}
            onClear={() => {
              setIsClearingIcon(true);
              setSearchText('');
              setTimeout(() => setIsClearingIcon(false), 500);
            }}
            searchIconSize={24}
            otherIconsSize={18}
            isSearching={isSearching}
            isClearingIcon={isClearingIcon}
          />
          <Box sx={{ cursor: 'pointer', display: 'flex' }} onClick={() => setIsFilterDrawerOpen((prev) => !prev)}>
            <FilterIcon />
          </Box>
          <Box sx={{ cursor: 'pointer', display: 'flex' }} onClick={() => {
            setIsInfoDrawerOpen(true);
            setSelectedNode(null);
          }}>
            <InfoIcon size={20} color="#39ACE7" />
          </Box>
        </>,
        portalTarget
      )}

    </div>
  );
}